import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { leaveService } from '@/services/leave/leaveService';
import { leaveQueryKeys } from './queryKeys';
import { accrualHistoryQueryKeys } from './useAccrualHistory';
import { useToast } from '@/hooks/use-toast';
import { notifyLeaveSubmitted, notifyLeaveStatus } from '@/services/notifications/triggerNotification';
import { devLog } from '@/utils/logger';
import type { CreateLeaveRequestInput, UpdateLeaveRequestInput } from '@/services/leave/types';

// Parse error message to provide user-friendly feedback
const parseErrorMessage = (error: Error): string => {
  const message = error.message || '';
  
  devLog('[useLeaveMutations] parseErrorMessage - Input:', message);
  
  // Preserve detailed backend messages that already contain specific balance info
  if (message.includes('days available') || 
      message.includes('After restoring') || 
      message.includes('hours available')) {
    devLog('[useLeaveMutations] parseErrorMessage - Preserving detailed message');
    return message;
  }
  
  // Only apply generic messages for vague/technical errors
  if (message.includes('invalid input syntax for type uuid')) {
    return 'Failed to submit leave request. Please try again or contact support.';
  }
  
  // Return the original message for all other cases (backend already sends good messages)
  return message || 'An unexpected error occurred. Please try again.';
};

export const useLeaveMutations = () => {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();
  const { toast } = useToast();

  const createLeaveRequest = useMutation({
    mutationFn: (request: CreateLeaveRequestInput) => {
      devLog('[useLeaveMutations] createLeaveRequest - Mutation started');
      
      if (!profile?.id) {
        console.error('[useLeaveMutations] createLeaveRequest - ERROR: No profile ID');
        throw new Error('User profile not found');
      }
      
      devLog('[useLeaveMutations] createLeaveRequest - Calling leaveService');
      return leaveService.createLeaveRequest({
        ...request,
        profile_id: profile.id,
        company_id: profile.company_id,
      });
    },
    onSuccess: (data, variables) => {
      devLog('[useLeaveMutations] createLeaveRequest - SUCCESS');
      
      // Trigger notification for managers (fire-and-forget)
      if (profile?.id && profile?.company_id) {
        notifyLeaveSubmitted({
          companyId: profile.company_id,
          leaveRequestId: data.id,
          leaveType: variables.leave_type,
          startDate: variables.start_date,
          endDate: variables.end_date,
          requesterProfileId: profile.id,
          requesterName: profile.first_name ? `${profile.first_name} ${profile.last_name || ''}`.trim() : undefined,
        });
      }
      
      // Invalidate relevant queries
      if (profile?.id) {
        queryClient.invalidateQueries({ queryKey: leaveQueryKeys.myRequests(profile.id) });
        queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances(profile.id) });
      }
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.companyRequests() });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      
      toast({
        title: "Leave Request Submitted",
        description: "Your leave request has been submitted successfully and is pending approval.",
      });
    },
    onError: (error: Error) => {
      console.error('[useLeaveMutations] createLeaveRequest - ERROR:', error);
      const userFriendlyMessage = parseErrorMessage(error);
      
      toast({
        title: "Unable to Submit Leave Request",
        description: userFriendlyMessage,
        variant: "destructive",
      });
    },
  });

  const updateLeaveRequestStatus = useMutation({
    mutationFn: ({ requestId, status, reason }: { requestId: string; status: 'approved' | 'rejected'; reason?: string }) => {
      devLog('[useLeaveMutations] updateLeaveRequestStatus - Starting:', { requestId, status });
      return leaveService.updateLeaveRequestStatus(requestId, status, reason);
    },
    onSuccess: (data, { requestId, status }) => {
      devLog('[useLeaveMutations] updateLeaveRequestStatus - SUCCESS:', status);
      
      // Trigger notification for leave status change (fire-and-forget)
      if (data && 'profileId' in data && data.profileId && data.companyId) {
        devLog('[useLeaveMutations] updateLeaveRequestStatus - Triggering notification');
        notifyLeaveStatus({
          companyId: data.companyId,
          leaveRequestId: requestId,
          status,
          requesterProfileId: data.profileId,
          leaveType: data.leaveType,
          startDate: data.startDate,
          endDate: data.endDate,
        });
      } else {
        console.warn('[useLeaveMutations] updateLeaveRequestStatus - Skipping notification (missing data)');
      }
      
      // Invalidate all leave request queries
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.requests() });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      // Invalidate accrual history to show the new audit trail entry
      queryClient.invalidateQueries({ queryKey: accrualHistoryQueryKeys.all });
      // Invalidate leave overlay queries (for schedule views)
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.leaveOverlay() });
      
      toast({
        title: "Status Updated",
        description: `Leave request ${status} successfully. Employee balance has been updated.`,
      });
    },
    onError: (error: Error) => {
      console.error('[useLeaveMutations] updateLeaveRequestStatus - ERROR:', error);
      toast({
        title: "Failed to Update Status",
        description: parseErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const adminUpdateLeaveRequestStatus = useMutation({
    mutationFn: ({ requestId, status, reason }: { requestId: string; status: string; reason?: string }) => {
      devLog('[useLeaveMutations] adminUpdateLeaveRequestStatus - Starting:', { requestId, status, hasReason: !!reason });
      return leaveService.adminUpdateLeaveRequestStatus(requestId, status, reason);
    },
    onSuccess: (data, { requestId, status }) => {
      devLog('[useLeaveMutations] adminUpdateLeaveRequestStatus - SUCCESS:', status);
      
      // Trigger notification for leave status change (fire-and-forget)
      if (data && 'profileId' in data && data.profileId && data.companyId) {
        devLog('[useLeaveMutations] adminUpdateLeaveRequestStatus - Triggering notification');
        notifyLeaveStatus({
          companyId: data.companyId,
          leaveRequestId: requestId,
          status,
          requesterProfileId: data.profileId,
          leaveType: data.leaveType,
          startDate: data.startDate,
          endDate: data.endDate,
        });
      } else {
        console.warn('[useLeaveMutations] adminUpdateLeaveRequestStatus - Skipping notification (missing data)');
      }
      
      // Invalidate all leave request queries
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.requests() });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      // Invalidate accrual history to show the new audit trail entry
      queryClient.invalidateQueries({ queryKey: accrualHistoryQueryKeys.all });
      // Invalidate leave overlay queries (for schedule views)
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.leaveOverlay() });
      
      toast({
        title: "Status Updated",
        description: `Leave request status changed to ${status}. Balance has been adjusted accordingly.`,
      });
    },
    onError: (error: Error) => {
      console.error('[useLeaveMutations] adminUpdateLeaveRequestStatus - ERROR:', error);
      toast({
        title: "Failed to Update Status",
        description: parseErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const updateMyLeaveRequest = useMutation({
    mutationFn: ({ requestId, request }: { requestId: string; request: UpdateLeaveRequestInput }) => {
      devLog('[useLeaveMutations] updateMyLeaveRequest - Starting:', { requestId });
      return leaveService.updateMyLeaveRequest(requestId, request);
    },
    onSuccess: () => {
      devLog('[useLeaveMutations] updateMyLeaveRequest - SUCCESS');
      
      if (profile?.id) {
        queryClient.invalidateQueries({ queryKey: leaveQueryKeys.myRequests(profile.id) });
        queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances(profile.id) });
      }
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.companyRequests() });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      
      toast({
        title: "Leave Request Updated",
        description: "Your leave request has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      console.error('[useLeaveMutations] updateMyLeaveRequest - ERROR:', error);
      toast({
        title: "Failed to Update Request",
        description: parseErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const cancelMyLeaveRequest = useMutation({
    mutationFn: (requestId: string) => {
      devLog('[useLeaveMutations] cancelMyLeaveRequest - Starting:', requestId);
      return leaveService.cancelMyLeaveRequest(requestId);
    },
    onSuccess: () => {
      devLog('[useLeaveMutations] cancelMyLeaveRequest - SUCCESS');
      
      if (profile?.id) {
        queryClient.invalidateQueries({ queryKey: leaveQueryKeys.myRequests(profile.id) });
        queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances(profile.id) });
      }
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.companyRequests() });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      
      toast({
        title: "Leave Request Cancelled",
        description: "Your leave request has been cancelled and pending balance restored.",
      });
    },
    onError: (error: Error) => {
      console.error('[useLeaveMutations] cancelMyLeaveRequest - ERROR:', error);
      toast({
        title: "Failed to Cancel Request",
        description: parseErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  // Edit approved leave request - changes to pending for re-approval
  const editApprovedLeaveRequest = useMutation({
    mutationFn: ({ requestId, request }: { requestId: string; request: UpdateLeaveRequestInput }) => {
      devLog('[useLeaveMutations] editApprovedLeaveRequest - Starting:', { requestId });
      return leaveService.editApprovedLeaveRequest(requestId, request);
    },
    onSuccess: (data) => {
      devLog('[useLeaveMutations] editApprovedLeaveRequest - SUCCESS');
      
      // Trigger notification for managers (fire-and-forget) - resubmission needs re-approval
      if (data && 'profileId' in data && data.profileId && data.companyId) {
        devLog('[useLeaveMutations] editApprovedLeaveRequest - Triggering manager notification');
        notifyLeaveSubmitted({
          companyId: data.companyId,
          leaveRequestId: data.requestId,
          leaveType: data.leaveType,
          startDate: data.startDate,
          endDate: data.endDate,
          requesterProfileId: data.profileId,
          requesterName: profile?.first_name ? `${profile.first_name} ${profile.last_name || ''}`.trim() : undefined,
        });
      } else {
        console.warn('[useLeaveMutations] editApprovedLeaveRequest - Skipping notification (missing data)');
      }
      
      if (profile?.id) {
        queryClient.invalidateQueries({ queryKey: leaveQueryKeys.myRequests(profile.id) });
        queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances(profile.id) });
      }
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.companyRequests() });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      // Invalidate accrual history to show the new audit trail entry
      queryClient.invalidateQueries({ queryKey: accrualHistoryQueryKeys.all });
      
      toast({
        title: "Leave Request Updated",
        description: "Your approved leave has been edited and is now pending re-approval. Balance has been adjusted.",
      });
    },
    onError: (error: Error) => {
      console.error('[useLeaveMutations] editApprovedLeaveRequest - ERROR:', error);
      toast({
        title: "Cannot Edit Leave Request",
        description: parseErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  // Cancel approved leave request - restores balance
  const cancelApprovedLeaveRequest = useMutation({
    mutationFn: (requestId: string) => {
      devLog('[useLeaveMutations] cancelApprovedLeaveRequest - Starting:', requestId);
      return leaveService.cancelApprovedLeaveRequest(requestId);
    },
    onSuccess: (data) => {
      devLog('[useLeaveMutations] cancelApprovedLeaveRequest - SUCCESS');
      
      if (profile?.id) {
        queryClient.invalidateQueries({ queryKey: leaveQueryKeys.myRequests(profile.id) });
        queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances(profile.id) });
      }
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.companyRequests() });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      // Invalidate accrual history to show the new audit trail entry
      queryClient.invalidateQueries({ queryKey: accrualHistoryQueryKeys.all });
      
      toast({
        title: "Approved Leave Cancelled",
        description: `Your approved leave has been cancelled. ${data.daysRestored} days have been restored to your balance.`,
      });
    },
    onError: (error: Error) => {
      console.error('[useLeaveMutations] cancelApprovedLeaveRequest - ERROR:', error);
      toast({
        title: "Failed to Cancel Approved Request",
        description: parseErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  return {
    createLeaveRequest,
    updateLeaveRequestStatus,
    adminUpdateLeaveRequestStatus,
    updateMyLeaveRequest,
    cancelMyLeaveRequest,
    editApprovedLeaveRequest,
    cancelApprovedLeaveRequest,
  };
};
